// src/Pages/finanzas-clientes/useExpensesSection.ts
import { useCallback, useEffect, useState } from "react";
import { getMsg } from "../../utils/antdStatic";
import { Dayjs } from "dayjs";
import { apiFetch } from "../../services/apiClient";
import { SECTION_CATEGORIES, SectionKey } from "./sections";

// El backend persiste `pagado_por`/`pagado_a` como JSONB con shape
// `{ tipo: 0..5, nombre?, usuario_id?, recipient_id? }`. El tipo viejo
// (`string`) en este archivo era incorrecto y rompía filtros que comparaban
// `=== "1"`. Ver docs/flujo-financiero.md §10.2.
export type ExpensePartyTipo = 0 | 1 | 2 | 3 | 4 | 5;
export type ExpenseParty = {
  tipo: ExpensePartyTipo;
  nombre?: string | null;
  usuario_id?: number | null;
  recipient_id?: number | null;
} | null;

export type ExpenseRow = {
  id: number;
  monto: number;
  fecha: string;
  pagado_por: ExpenseParty;
  pagado_a: ExpenseParty;
  rol_de_aloja_facil: "paga" | "recibe" | "intermediario";
  descripcion: string;
  propiedad_id: number | null;
  categoria: string;
  imagenes?: string[] | null;
  nombre?: string;
  cantidadDeUnidades?: number;
  cantidadDePacks?: number;
};

// Subtotales con signo desde la perspectiva del Cliente. Los provee el
// backend (`/finanzas/gastos/:id`) en `totals.{ingresoCliente,egresoCliente,neutro}`.
export type ExpenseTotals = {
  monto: number;
  currency: string;
  ingresoCliente?: number;
  egresoCliente?: number;
  neutro?: number;
};

export function useExpensesSection(
  clientId: number | null | undefined,
  selectedPropId: number | null,
  section: SectionKey
) {
  const [rows, setRows] = useState<ExpenseRow[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [size, setSize] = useState(20);
  const [loading, setLoading] = useState(false);
  const [totals, setTotals] = useState<ExpenseTotals | null>(null);
  const [month, setMonth] = useState<Dayjs | null>(null);
  const [q, setQ] = useState<string | undefined>(undefined);

  const fetchRows = useCallback(
    async (p = page, s = size) => {
      if (!selectedPropId) return;
      try {
        setLoading(true);
        const params = new URLSearchParams();
        params.set("page", String(p));
        params.set("size", String(s));
        params.set("propId", String(selectedPropId));
        params.set("withTotals", "true");
        // Lista vacía (ej. sección "otros" tras el rediseño 2026-06-05) =
        // SIN filtro de categoría → trae TODOS los flujos de la propiedad.
        const cats = SECTION_CATEGORIES[section];
        if (cats && cats.length) params.set("categoria", cats.join(","));
        if (q) params.set("q", q);
        if (month) {
          // Filtra por mes completo
          const from = month.startOf("month").toISOString();
          const to = month.endOf("month").toISOString();
          params.set("from", from);
          params.set("to", to);
        }

        if (!clientId || !selectedPropId) return;

        // La auth viaja por cookie HttpOnly (`apiFetch` la incluye con
        // `credentials: "include"`). Sin sesión, el interceptor global
        // dispara `forceLogout()` — comportamiento esperado.
        const json = await apiFetch<any>(
          `/finanzas/gastos/${clientId}?${params.toString()}`,
        );
        if (!json) throw new Error("No se pudo obtener gastos");

        setRows(json.data || []);
        setTotal(json.total || 0);
        setPage(json.page || 1);
        setSize(json.size || 20);
        setTotals(json.totals || null);
      } catch (e: any) {
        getMsg()?.error(e?.message || "Error cargando gastos");
      } finally {
        setLoading(false);
      }
    },
    [selectedPropId, section, page, size, q, month]
  );

  useEffect(() => {
    if (clientId && selectedPropId) fetchRows(1, size);
  }, [clientId, selectedPropId, section]); // eslint-disable-line react-hooks/exhaustive-deps

  // opcional: si cambias q/month, reinicia a página 1
  useEffect(() => {
    if (clientId && selectedPropId) fetchRows(1, size);
  }, [q, month]);

  useEffect(() => {
    if (selectedPropId) fetchRows(1, size);
  }, [selectedPropId, section]);

  return {
    rows,
    total,
    page,
    size,
    loading,
    totals,
    month,
    setMonth,
    q,
    setQ,
    onPaginate: (p: number, s?: number) => fetchRows(p, s ?? size),
    refetch: fetchRows,
  };
}
